import Phaser from 'phaser';
import type {
  GameDefinition, ExplorationMap, AvatarConfig, CompanionConfig,
  ProximityInteraction
} from '../types/game';

const BASE_W = 1280;
const BASE_H = 720;

const DEPTH = {
  bg: 0,
  tint: 1,
  ambiance: 6,
  objects: 8,
  npc: 9,
  player: 10,
  companion: 10,
  prompt: 20,
  vignette: 60,
  fade: 100
};

export interface ExplorationViewCallbacks {
  onInteract: (id: string) => void;
  onBlocked: (hint: string) => void;
  onSequenceSfx?: (sound: string, volume?: number) => void;
  onMapEnter?: (mapId: string) => void;
  onCheckCondition?: (condition: unknown) => boolean;
  /**
   * Disponibilidad real de una interacción según el motor. La vista no puede
   * deducirla de `visibleIf` porque sólo el estado guarda qué interacciones
   * `once` ya se consumieron.
   */
  onInteractionAvailable?: (id: string) => boolean;
}

interface PlayerState {
  sprite: Phaser.GameObjects.Sprite | null;
  x: number;
  y: number;
  facing: 'down' | 'up' | 'left' | 'right';
  moving: boolean;
}

interface CompanionState {
  sprite: Phaser.GameObjects.Sprite | null;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  visible: boolean;
}

export class ExplorationView {
  private game: Phaser.Game | null = null;
  private sceneInstance: Phaser.Scene | null = null;
  private reducedMotion = false;
  private currentMapId: string | null = null;

  private player: PlayerState = { sprite: null, x: 0, y: 0, facing: 'down', moving: false };
  private companion: CompanionState = { sprite: null, x: 0, y: 0, targetX: 0, targetY: 0, visible: false };

  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  private wasd: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key } | null = null;
  private interactKey: Phaser.Input.Keyboard.Key | null = null;

  private collisionBodies: Phaser.GameObjects.Rectangle[] = [];
  private interactionPrompts: Map<string, Phaser.GameObjects.Container> = new Map();
  private interactionBadges: Map<string, { bg: Phaser.GameObjects.Graphics; txt: Phaser.GameObjects.Text }> = new Map();
  private portalMarkers: Map<string, Phaser.GameObjects.Container> = new Map();
  private companionDepth: number = DEPTH.companion + 1;
  private portalGuard: string | null = null;
  private nearbyInteraction: ProximityInteraction | null = null;
  private promptBubble: Phaser.GameObjects.Container | null = null;
  private visibilityClock = 0;

  private objectSprites: Map<string, Phaser.GameObjects.Image> = new Map();
  private npcSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private createdAnimations: Set<string> = new Set();

  private fadeRect: Phaser.GameObjects.Rectangle | null = null;
  private liveTweens: Phaser.Tweens.Tween[] = [];

  private moveEnabled = true;
  private touchJoystick: { active: boolean; startX: number; startY: number; dx: number; dy: number } = {
    active: false, startX: 0, startY: 0, dx: 0, dy: 0
  };
  private pendingMap: { mapId: string; spawn?: string } | null = null;

  private walkGrid: { cols: number; rows: number; cell: number; blocked: Uint8Array } | null = null;
  private movePath: Array<{ x: number; y: number }> = [];
  private pendingInteractionId: string | null = null;
  private destMarker: Phaser.GameObjects.Container | null = null;
  private pointerGesture: { startX: number; startY: number; dragging: boolean; pointerId: number } | null = null;

  constructor(
    private readonly parent: HTMLElement,
    private readonly def: GameDefinition,
    private readonly callbacks: ExplorationViewCallbacks,
    private readonly assetBaseUrl = ''
  ) {}

  create(): void {
    const self = this;
    const images = this.def.assets.images ?? {};

    class ExplorationScene extends Phaser.Scene {
      constructor() { super('exploration'); }
      preload(): void {
        for (const [key, entry] of Object.entries(images)) {
          const src = entry.src.startsWith('data:') ? entry.src : self.assetBaseUrl + entry.src;
          if (entry.kind === 'spritesheet' && entry.frameWidth && entry.frameHeight) {
            this.load.spritesheet(key, src, { frameWidth: entry.frameWidth, frameHeight: entry.frameHeight });
          } else {
            this.load.image(key, src);
          }
        }
      }
      create(): void {
        self.sceneInstance = this;
        self.setupInput(this);
        self.createOverlays(this);
        self.setupTouchControls(this);
        if (self.pendingMap) {
          const { mapId, spawn } = self.pendingMap;
          self.pendingMap = null;
          self.loadMap(mapId, spawn);
        }
      }
      update(_time: number, delta: number): void {
        self.updatePlayer(delta / 1000);
        self.updateCompanion(delta / 1000);
        self.checkProximity();
        self.updateDepthSorting();
        self.visibilityClock += delta;
        if (self.visibilityClock >= 120) {
          self.visibilityClock = 0;
          self.refreshVisibility();
        }
      }
    }

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.parent,
      width: BASE_W,
      height: BASE_H,
      backgroundColor: '#05070f',
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: BASE_W,
        height: BASE_H
      },
      scene: [ExplorationScene]
    });
    (window as unknown as { __ae?: ExplorationView }).__ae = this;
    this.parent.setAttribute('aria-hidden', 'true');
  }

  private setupInput(scene: Phaser.Scene): void {
    if (scene.input.keyboard) {
      this.cursors = scene.input.keyboard.createCursorKeys();
      this.wasd = scene.input.keyboard.addKeys('W,A,S,D') as typeof this.wasd;
      this.interactKey = scene.input.keyboard.addKey('E');
      this.interactKey.on('down', () => this.tryInteract());

      scene.input.keyboard.on('keydown-SPACE', () => this.tryInteract());

      // Phaser llama a preventDefault de estas teclas incluso con un <input>
      // enfocado, lo que impedía escribir esas letras en los minijuegos de texto.
      scene.input.keyboard.removeCapture(['UP', 'DOWN', 'LEFT', 'RIGHT', 'SPACE', 'W', 'A', 'S', 'D', 'E']);
      window.addEventListener('keydown', this.blockScrollKeys);
      document.addEventListener('keydown', this.guardGameKeys);
    }
  }

  /** Teclas que Phaser tiene registradas (movimiento e interacción). */
  private static readonly GAME_KEYS = new Set([
    'w', 'a', 's', 'd', 'e', ' ', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'
  ]);
  /** Overlays que ocupan la pantalla por encima de la exploración. */
  private static readonly OVERLAY_SELECTOR =
    '.ae-mg-overlay, .ae-modal-backdrop, .ae-video-overlay, .ae-video-tv';

  /**
   * Deja que la pulsación llegue a su destino (p. ej. un campo de texto) pero
   * corta la burbuja hacia el listener de Phaser en window: sin esto, escribir
   * una E o un espacio dentro de un minijuego disparaba interactuar/moverse.
   */
  private guardGameKeys = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement | null;
    const enCampo =
      !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
    const hayOverlay = document.querySelector(ExplorationView.OVERLAY_SELECTOR) !== null;
    if (!enCampo && !hayOverlay) return;
    if (!ExplorationView.GAME_KEYS.has(event.key.toLowerCase())) return;
    event.stopPropagation();
    if (hayOverlay && !enCampo && (event.key === ' ' || event.key.startsWith('Arrow'))) event.preventDefault();
  };

  /** Evita el scroll de la página con espacio/flechas, salvo al escribir en un campo. */
  private blockScrollKeys = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (event.key === ' ' || event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
    }
  };

  private setupTouchControls(scene: Phaser.Scene): void {
    const TAP_SLOP = 14;
    scene.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.pointerGesture = { startX: p.x, startY: p.y, dragging: false, pointerId: p.id };
    });
    scene.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      const gs = this.pointerGesture;
      if (!gs || p.id !== gs.pointerId) return;
      if (!gs.dragging && Math.hypot(p.x - gs.startX, p.y - gs.startY) > TAP_SLOP) {
        gs.dragging = true;
        this.cancelClickMove();
        if (p.wasTouch) {
          this.touchJoystick.active = true;
          this.touchJoystick.startX = gs.startX;
          this.touchJoystick.startY = gs.startY;
          this.touchJoystick.dx = 0;
          this.touchJoystick.dy = 0;
        }
      }
      if (gs.dragging && this.touchJoystick.active) {
        this.touchJoystick.dx = (p.x - this.touchJoystick.startX) / 60;
        this.touchJoystick.dy = (p.y - this.touchJoystick.startY) / 60;
        const mag = Math.sqrt(this.touchJoystick.dx ** 2 + this.touchJoystick.dy ** 2);
        if (mag > 1) {
          this.touchJoystick.dx /= mag;
          this.touchJoystick.dy /= mag;
        }
      }
    });
    const endPointer = (p: Phaser.Input.Pointer) => {
      const gs = this.pointerGesture;
      if (!gs || p.id !== gs.pointerId) return;
      if (this.touchJoystick.active) {
        this.touchJoystick.active = false;
        this.touchJoystick.dx = 0;
        this.touchJoystick.dy = 0;
      }
      const wasTap = !gs.dragging;
      this.pointerGesture = null;
      if (wasTap) {
        const wp = scene.cameras.main.getWorldPoint(p.x, p.y);
        this.handleTap(wp.x, wp.y);
      }
    };
    scene.input.on('pointerup', endPointer);
    scene.input.on('pointerupoutside', endPointer);
  }

  private createOverlays(scene: Phaser.Scene): void {
    if (!scene.textures.exists('ae-vignette')) {
      const tex = scene.textures.createCanvas('ae-vignette', BASE_W, BASE_H);
      if (tex) {
        const ctx = tex.getContext();
        const grad = ctx.createRadialGradient(
          BASE_W / 2, BASE_H / 2, BASE_H * 0.26,
          BASE_W / 2, BASE_H / 2, BASE_H * 0.92
        );
        grad.addColorStop(0, 'rgba(0,0,0,0)');
        grad.addColorStop(0.6, 'rgba(0,0,0,0.12)');
        grad.addColorStop(1, 'rgba(0,0,0,0.62)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, BASE_W, BASE_H);
        tex.refresh();
      }
    }
    if (scene.textures.exists('ae-vignette')) {
      scene.add.image(BASE_W / 2, BASE_H / 2, 'ae-vignette')
        .setDepth(DEPTH.vignette).setAlpha(0.9);
    }
    this.fadeRect = scene.add.rectangle(BASE_W / 2, BASE_H / 2, BASE_W, BASE_H, 0x04060d, 1)
      .setDepth(DEPTH.fade);
  }

  loadMap(mapId: string, spawnPoint?: string): void {
    if (!this.sceneInstance) {
      this.pendingMap = { mapId, spawn: spawnPoint };
      return;
    }
    const scene = this.sceneInstance;
    const mapDef = this.def.maps?.[mapId];
    if (!mapDef) return;

    this.clearMap();
    const prevMapId = this.currentMapId;
    this.currentMapId = mapId;

    const cam = scene.cameras.main;
    cam.setBounds(0, 0, mapDef.width, mapDef.height);

    this.buildBackground(scene, mapDef);
    this.buildCollisions(scene, mapDef);
    this.buildWalkGrid(mapDef);
    this.buildObjects(scene, mapDef);
    this.buildNpcs(scene, mapDef);
    this.buildPlayer(scene, mapDef, spawnPoint, prevMapId ?? undefined);
    this.buildCompanion(scene, mapDef);
    this.buildInteractionPrompts(scene, mapDef);
    this.buildPortalMarkers(scene, mapDef);

    cam.centerOn(this.player.x, this.player.y);

    if (!this.reducedMotion && this.fadeRect) {
      this.fadeRect.setAlpha(1);
      const t = scene.tweens.add({
        targets: this.fadeRect, alpha: 0, duration: 400, ease: 'Sine.easeOut'
      });
      this.liveTweens.push(t);
    } else if (this.fadeRect) {
      this.fadeRect.setAlpha(0);
    }

    this.callbacks.onMapEnter?.(mapId);
  }

  private clearMap(): void {
    this.clearTweens();
    this.cancelClickMove();
    this.walkGrid = null;
    if (this.player.sprite) { this.player.sprite.destroy(); this.player.sprite = null; }
    if (this.companion.sprite) { this.companion.sprite.destroy(); this.companion.sprite = null; }
    for (const b of this.collisionBodies) b.destroy();
    this.collisionBodies = [];
    for (const s of this.objectSprites.values()) s.destroy();
    this.objectSprites.clear();
    for (const s of this.npcSprites.values()) s.destroy();
    this.npcSprites.clear();
    for (const p of this.interactionPrompts.values()) p.destroy();
    this.interactionPrompts.clear();
    this.interactionBadges.clear();
    for (const m of this.portalMarkers.values()) m.destroy();
    this.portalMarkers.clear();
    if (this.promptBubble) { this.promptBubble.destroy(); this.promptBubble = null; }
    this.nearbyInteraction = null;
  }

  private conditionMet(condition: unknown): boolean {
    if (!condition || !this.callbacks.onCheckCondition) return true;
    return this.callbacks.onCheckCondition(condition);
  }

  /**
   * Disponibilidad real de una interacción. Se pregunta al motor cuando puede
   * responder porque sólo el estado sabe si una interacción `once` ya se consumió;
   * mirar únicamente `visibleIf` dejaría en el mapa puntos luminosos muertos.
   */
  private interactionAvailable(inter: ProximityInteraction): boolean {
    if (this.callbacks.onInteractionAvailable) return this.callbacks.onInteractionAvailable(inter.id);
    return this.conditionMet(inter.visibleIf);
  }

  /**
   * Reevalúa todo lo que depende del estado: puntos de interacción, puertas, NPCs
   * y el compañero. Se construyen al cargar el mapa, pero el estado cambia mientras
   * se juega y el mapa no se recarga; sin este repaso una puerta que sólo se abre
   * al cumplir un objetivo no aparecería nunca.
   */
  private refreshVisibility(): void {
    const mapDef = this.currentMap();
    if (!mapDef) return;

    for (const [id, inter] of Object.entries(mapDef.interactions ?? {})) {
      this.interactionPrompts.get(id)?.setVisible(this.interactionAvailable(inter));
      if (this.interactionBadges.has(id)) this.styleBadge(id, this.conditionMet(inter.enabledIf));
    }
    for (const [id, obj] of Object.entries(mapDef.objects ?? {})) {
      this.objectSprites.get(id)?.setVisible(this.conditionMet(obj.visibleIf));
    }
    for (const [id, portal] of Object.entries(mapDef.portals ?? {})) {
      this.portalMarkers.get(id)?.setVisible(this.conditionMet(portal.visibleIf));
    }
    for (const [id, npc] of Object.entries(mapDef.npcs ?? {})) {
      this.npcSprites.get(id)?.setVisible(this.conditionMet(npc.visibleIf));
    }

    const comp = this.def.exploration?.companion;
    if (!comp || !this.companion.sprite) return;
    const visible = this.conditionMet(comp.visibleIf);
    if (visible && !this.companion.visible) {
      this.companion.x = this.player.x - 60;
      this.companion.y = this.player.y;
      this.companion.targetX = this.companion.x;
      this.companion.targetY = this.companion.y;
      this.companion.sprite.setPosition(this.companion.x, this.companion.y);
    }
    this.companion.visible = visible;
    this.companion.sprite.setVisible(visible);
  }

  private buildBackground(scene: Phaser.Scene, mapDef: ExplorationMap): void {
    if (mapDef.background && scene.textures.exists(mapDef.background)) {
      scene.add.image(mapDef.width / 2, mapDef.height / 2, mapDef.background)
        .setDisplaySize(mapDef.width, mapDef.height)
        .setDepth(DEPTH.bg);
    }
    if (mapDef.tint) {
      const color = Phaser.Display.Color.HexStringToColor(mapDef.tint).color;
      scene.add.rectangle(mapDef.width / 2, mapDef.height / 2, mapDef.width, mapDef.height, color, 0.22)
        .setBlendMode(Phaser.BlendModes.MULTIPLY)
        .setDepth(DEPTH.tint);
    }
  }

  private buildCollisions(scene: Phaser.Scene, mapDef: ExplorationMap): void {
    if (!mapDef.collisions) return;
    for (const col of mapDef.collisions) {
      const x = col.x * mapDef.width;
      const y = col.y * mapDef.height;
      const w = col.w * mapDef.width;
      const h = col.h * mapDef.height;
      const rect = scene.add.rectangle(x + w / 2, y + h / 2, w, h, 0x000000, 0)
        .setDepth(DEPTH.objects);
      this.collisionBodies.push(rect);
    }
  }

  private buildObjects(scene: Phaser.Scene, mapDef: ExplorationMap): void {
    if (!mapDef.objects) return;
    for (const [objId, obj] of Object.entries(mapDef.objects)) {
      const stateDef = obj.states[obj.initialState];
      if (!stateDef || !scene.textures.exists(stateDef.image)) continue;
      const x = stateDef.position.x * mapDef.width;
      const y = stateDef.position.y * mapDef.height;
      const sprite = scene.add.image(x, y, stateDef.image).setDepth(DEPTH.objects);
      if (stateDef.scale) sprite.setScale(stateDef.scale);
      sprite.setVisible(this.conditionMet(obj.visibleIf));
      this.objectSprites.set(objId, sprite);
    }
  }

  private buildNpcs(scene: Phaser.Scene, mapDef: ExplorationMap): void {
    if (!mapDef.npcs) return;
    for (const [npcId, npc] of Object.entries(mapDef.npcs)) {
      const charDef = this.def.characters[npc.character];
      if (!charDef?.spriteSheet || !scene.textures.exists(charDef.spriteSheet)) continue;

      this.ensureSpriteAnimations(scene, charDef.spriteSheet, charDef.id, charDef.animations, charDef.spriteFrameWidth);

      const x = npc.position.x * mapDef.width;
      const y = npc.position.y * mapDef.height;
      const scale = npc.scale ?? charDef.spriteScale ?? 1;
      const sprite = scene.add.sprite(x, y, charDef.spriteSheet)
        .setScale(scale)
        .setDepth(DEPTH.npc);
      if (charDef.spriteAnchor === 'feet') sprite.setOrigin(0.5, 1);
      sprite.setVisible(this.conditionMet(npc.visibleIf));

      const animKey = `${charDef.id}-${npc.animation ?? 'idle'}`;
      if (scene.anims.exists(animKey)) {
        sprite.play(animKey);
      }

      this.npcSprites.set(npcId, sprite);
    }
  }

  private buildPlayer(scene: Phaser.Scene, mapDef: ExplorationMap, spawnPoint?: string, prevMapId?: string): void {
    const expl = this.def.exploration;
    if (!expl) return;
    const avatar = expl.avatar;
    if (!scene.textures.exists(avatar.spriteSheet)) return;

    this.ensureAvatarAnimations(scene, avatar);

    let spawn = spawnPoint && mapDef.spawnPoints?.[spawnPoint]
      ? mapDef.spawnPoints[spawnPoint]
      : null;
    if (!spawn && prevMapId) {
      const back = Object.values(mapDef.portals ?? {}).find(p => p.targetMap === prevMapId);
      if (back) {
        const dx = 0.5 - back.position.x;
        const dy = 0.5 - back.position.y;
        spawn = Math.abs(dx) >= Math.abs(dy)
          ? { x: back.position.x + Math.sign(dx || 1) * 0.08, y: back.position.y }
          : { x: back.position.x, y: back.position.y + Math.sign(dy || 1) * 0.08 };
      }
    }
    if (!spawn) spawn = { x: 0.5, y: 0.5 };

    this.player.x = spawn.x * mapDef.width;
    this.player.y = spawn.y * mapDef.height;
    this.snapToWalkable();
    this.player.facing = 'down';

    const scale = avatar.scale ?? 1;
    this.player.sprite = scene.add.sprite(this.player.x, this.player.y, avatar.spriteSheet)
      .setScale(scale)
      .setDepth(DEPTH.player);
    this.player.sprite.setOrigin(0.5, 1);

    const idleKey = `avatar-${avatar.animations.idle}`;
    if (scene.anims.exists(idleKey)) {
      this.player.sprite.play(idleKey);
    }

    scene.cameras.main.startFollow(this.player.sprite, true, 0.08, 0.08);
    scene.cameras.main.setScroll(this.player.x - BASE_W / 2, this.player.y - BASE_H / 2);
  }

  private buildCompanion(scene: Phaser.Scene, _mapDef: ExplorationMap): void {
    const expl = this.def.exploration;
    if (!expl?.companion) return;
    const comp = expl.companion;
    if (!scene.textures.exists(comp.spriteSheet)) return;

    this.ensureCompanionAnimations(scene, comp);

    this.companion.x = this.player.x - 60;
    this.companion.y = this.player.y;
    this.companion.targetX = this.companion.x;
    this.companion.targetY = this.companion.y;
    this.companion.visible = this.conditionMet(comp.visibleIf);

    const scale = comp.scale ?? 1;
    this.companion.sprite = scene.add.sprite(this.companion.x, this.companion.y, comp.spriteSheet)
      .setScale(scale)
      .setDepth(DEPTH.companion);
    this.companion.sprite.setOrigin(0.5, 1);
    this.companion.sprite.setVisible(this.companion.visible);

    const idleKey = `companion-${comp.animations.idle}`;
    if (scene.anims.exists(idleKey)) {
      this.companion.sprite.play(idleKey);
    }
  }

  private buildInteractionPrompts(scene: Phaser.Scene, mapDef: ExplorationMap): void {
    if (!mapDef.interactions) return;
    for (const [id, inter] of Object.entries(mapDef.interactions)) {
      const x = inter.position.x * mapDef.width;
      const y = inter.position.y * mapDef.height;
      const container = scene.add.container(x, y).setDepth(DEPTH.prompt);

      const indicator = scene.add.graphics();
      indicator.fillStyle(0xffd479, 0.3);
      indicator.fillCircle(0, 0, 12);
      indicator.lineStyle(2, 0xffd479, 0.8);
      indicator.strokeCircle(0, 0, 12);
      container.add(indicator);

      if (inter.badge) {
        const bg = scene.add.graphics();
        const txt = scene.add.text(0, -34, inter.badge, {
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '18px',
          fontStyle: '700',
          color: '#1a1206'
        }).setOrigin(0.5, 0.5);
        container.add(bg);
        container.add(txt);
        this.interactionBadges.set(id, { bg, txt });
        this.styleBadge(id, this.conditionMet(inter.enabledIf));
      }

      if (!this.reducedMotion) {
        const t = scene.tweens.add({
          targets: container, alpha: { from: 0.6, to: 1 },
          duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
        });
        this.liveTweens.push(t);
      }

      this.interactionPrompts.set(id, container);
      container.setVisible(this.interactionAvailable(inter));
    }
  }

  /**
   * Pinta la insignia de un marcador: brillante cuando la interacción está
   * habilitada (paso actual) y tenue cuando no (paso futuro/bloqueado). Así el
   * recorrido ordenado se lee de un vistazo: el número iluminado es el siguiente.
   */
  private styleBadge(id: string, enabled: boolean): void {
    const b = this.interactionBadges.get(id);
    if (!b) return;
    b.bg.clear();
    if (enabled) {
      b.bg.fillStyle(0xffd479, 1);
      b.bg.lineStyle(3, 0xfff3d6, 1);
      b.txt.setColor('#1a1206').setAlpha(1);
    } else {
      b.bg.fillStyle(0x2a3550, 0.92);
      b.bg.lineStyle(2, 0x5b6c8c, 0.9);
      b.txt.setColor('#9fb0d0').setAlpha(0.92);
    }
    b.bg.fillCircle(0, -34, 16);
    b.bg.strokeCircle(0, -34, 16);
  }

  /** Señal de acceso visible en cada portal: arco de puerta + flecha + rótulo del destino. */
  private buildPortalMarkers(scene: Phaser.Scene, mapDef: ExplorationMap): void {
    if (!mapDef.portals) return;
    for (const [portalId, portal] of Object.entries(mapDef.portals)) {
      const px = portal.position.x * mapDef.width;
      const py = portal.position.y * mapDef.height;
      const container = scene.add.container(px, py).setDepth(DEPTH.prompt);

      const arch = scene.add.graphics();
      arch.fillStyle(0x0a1622, 0.55);
      arch.lineStyle(3, 0x6fe3c4, 0.9);
      arch.beginPath();
      arch.moveTo(-28, 4);
      arch.lineTo(-28, -56);
      arch.arc(0, -56, 28, Math.PI, 0, false);
      arch.lineTo(28, 4);
      arch.closePath();
      arch.fillPath();
      arch.strokePath();
      container.add(arch);

      const arrow = scene.add.graphics();
      arrow.fillStyle(0x6fe3c4, 1);
      arrow.fillTriangle(-9, -98, 9, -98, 0, -86);
      arrow.fillTriangle(-9, -86, 9, -86, 0, -74);
      container.add(arrow);

      const label = this.mapDisplayName(portal.targetMap);
      const text = scene.add.text(0, 20, label, {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '15px',
        fontStyle: '600',
        color: '#eafff8',
        align: 'center',
        wordWrap: { width: 170 }
      }).setOrigin(0.5, 0);
      const plate = scene.add.graphics();
      const plateW = text.width + 16;
      const plateH = text.height + 12;
      plate.fillStyle(0x0a1622, 0.82);
      plate.lineStyle(2, 0x6fe3c4, 0.6);
      plate.fillRoundedRect(-plateW / 2, 14, plateW, plateH, 8);
      plate.strokeRoundedRect(-plateW / 2, 14, plateW, plateH, 8);
      container.add(plate);
      container.add(text);

      if (!this.reducedMotion) {
        const bounce = scene.tweens.add({
          targets: arrow, y: { from: -4, to: 4 },
          duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
        });
        this.liveTweens.push(bounce);
        const pulse = scene.tweens.add({
          targets: container, alpha: { from: 0.85, to: 1 },
          duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
        });
        this.liveTweens.push(pulse);
      }

      this.portalMarkers.set(portalId, container);
      container.setVisible(this.conditionMet(portal.visibleIf));
    }
  }

  private mapDisplayName(mapId: string): string {
    const name = this.def.maps?.[mapId]?.name;
    if (name) return name;
    const human = mapId.replace(/_/g, ' ');
    return human.charAt(0).toUpperCase() + human.slice(1);
  }

  private ensureAvatarAnimations(scene: Phaser.Scene, avatar: AvatarConfig): void {
    const prefix = 'ae-avatar';
    if (this.createdAnimations.has(prefix)) return;

    const cols = this.sheetCols(scene, avatar.spriteSheet, avatar.frameWidth);
    const anims = [
      ['idle', avatar.animations.idle, 0],
      ['walkDown', avatar.animations.walkDown, 1],
      ['walkUp', avatar.animations.walkUp, 2],
      ['walkLeft', avatar.animations.walkLeft, 3],
      ['walkRight', avatar.animations.walkRight, 4]
    ];
    for (const [name, frameKey, row] of anims) {
      const key = `avatar-${frameKey}`;
      if (!scene.anims.exists(key)) {
        const startFrame = (row as number) * cols;
        scene.anims.create({
          key,
          frames: scene.anims.generateFrameNumbers(avatar.spriteSheet, { start: startFrame, end: startFrame + cols - 1 }),
          frameRate: name === 'idle' ? 6 : 8,
          repeat: -1
        });
      }
    }
    this.createdAnimations.add(prefix);
  }

  private sheetCols(scene: Phaser.Scene, sheetKey: string, frameWidth?: number): number {
    const texture = scene.textures.get(sheetKey);
    const fw = frameWidth ?? texture.source[0].width;
    return Math.max(1, Math.floor(texture.source[0].width / fw));
  }

  private ensureCompanionAnimations(scene: Phaser.Scene, comp: CompanionConfig): void {
    const prefix = 'ae-companion';
    if (this.createdAnimations.has(prefix)) return;

    const cols = this.sheetCols(scene, comp.spriteSheet, comp.frameWidth);
    const anims = [
      ['idle', comp.animations.idle, 0],
      ['walk', comp.animations.walk, 1]
    ];
    for (const [name, frameKey, row] of anims) {
      const key = `companion-${frameKey}`;
      if (!scene.anims.exists(key)) {
        const startFrame = (row as number) * cols;
        scene.anims.create({
          key,
          frames: scene.anims.generateFrameNumbers(comp.spriteSheet, { start: startFrame, end: startFrame + cols - 1 }),
          frameRate: name === 'idle' ? 6 : 10,
          repeat: -1
        });
      }
    }
    this.createdAnimations.add(prefix);
  }

  private ensureSpriteAnimations(
    scene: Phaser.Scene, sheetKey: string, charId: string,
    animations?: Record<string, string>, frameWidth?: number
  ): void {
    if (!animations) return;
    const prefix = `ae-npc-${charId}`;
    if (this.createdAnimations.has(prefix)) return;

    const cols = this.sheetCols(scene, sheetKey, frameWidth);
    const animRows: Record<string, number> = {
      idle: 0,
      walk_down: 1,
      walk_up: 2,
      walk_left: 3,
      walk_right: 4,
      walk: 1,
      trapped: 5,
      happy: 6
    };

    for (const [animName, frameKey] of Object.entries(animations)) {
      const key = `${charId}-${animName}`;
      if (!scene.anims.exists(key)) {
        const row = animRows[frameKey] ?? 0;
        const startFrame = row * cols;
        scene.anims.create({
          key,
          frames: scene.anims.generateFrameNumbers(sheetKey, { start: startFrame, end: startFrame + cols - 1 }),
          frameRate: 8,
          repeat: -1
        });
      }
    }
    this.createdAnimations.add(prefix);
  }

  private updatePlayer(dt: number): void {
    if (!this.moveEnabled || !this.player.sprite) return;
    const mapDef = this.currentMap();
    if (!mapDef) return;
    const avatar = this.def.exploration?.avatar;
    if (!avatar) return;

    let dx = 0, dy = 0;
    if (this.cursors?.left?.isDown || this.wasd?.A?.isDown) dx -= 1;
    if (this.cursors?.right?.isDown || this.wasd?.D?.isDown) dx += 1;
    if (this.cursors?.up?.isDown || this.wasd?.W?.isDown) dy -= 1;
    if (this.cursors?.down?.isDown || this.wasd?.S?.isDown) dy += 1;
    const keyActive = dx !== 0 || dy !== 0;

    if (this.touchJoystick.active) {
      dx = this.touchJoystick.dx;
      dy = this.touchJoystick.dy;
    }

    const directActive = keyActive || this.touchJoystick.active;
    if (directActive) this.cancelClickMove();

    let moving = false;
    let targetX = this.player.x;
    let targetY = this.player.y;

    if (directActive) {
      const mag = Math.sqrt(dx * dx + dy * dy);
      if (mag > 0.1) {
        dx /= mag;
        dy /= mag;
        moving = true;
        targetX = this.player.x + dx * avatar.speed * dt;
        targetY = this.player.y + dy * avatar.speed * dt;
      }
    } else if (this.movePath.length > 0) {
      const wp = this.movePath[0];
      const ddx = wp.x - this.player.x;
      const ddy = wp.y - this.player.y;
      const dist = Math.sqrt(ddx * ddx + ddy * ddy);
      if (dist < 6) {
        this.movePath.shift();
        if (this.movePath.length === 0) this.onReachDestination();
      } else {
        dx = ddx / dist;
        dy = ddy / dist;
        moving = true;
        const step = Math.min(dist, avatar.speed * dt);
        targetX = this.player.x + dx * step;
        targetY = this.player.y + dy * step;
      }
    }

    const clampedX = Phaser.Math.Clamp(targetX, 20, mapDef.width - 20);
    const clampedY = Phaser.Math.Clamp(targetY, 20, mapDef.height - 20);

    if (!this.checkCollision(clampedX, this.player.y, 16)) {
      this.player.x = clampedX;
    }
    if (!this.checkCollision(this.player.x, clampedY, 16)) {
      this.player.y = clampedY;
    }

    this.player.sprite.setPosition(this.player.x, this.player.y);

    this.player.moving = moving;

    if (moving) {
      if (Math.abs(dx) > Math.abs(dy)) {
        this.player.facing = dx > 0 ? 'right' : 'left';
      } else {
        this.player.facing = dy > 0 ? 'down' : 'up';
      }

      const walkKey = `avatar-${avatar.animations[`walk${this.capitalize(this.player.facing)}` as keyof typeof avatar.animations]}`;
      if (this.player.sprite.anims.currentAnim?.key !== walkKey) {
        this.player.sprite.play(walkKey, true);
      }
    } else {
      const idleKey = `avatar-${avatar.animations.idle}`;
      if (this.player.sprite.anims.currentAnim?.key !== idleKey) {
        this.player.sprite.play(idleKey, true);
      }
    }

    this.checkPortals();
  }

  private capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  private checkCollision(x: number, y: number, radius: number): boolean {
    for (const body of this.collisionBodies) {
      const bx = body.x - body.width / 2;
      const by = body.y - body.height / 2;
      const bw = body.width;
      const bh = body.height;

      const closestX = Phaser.Math.Clamp(x, bx, bx + bw);
      const closestY = Phaser.Math.Clamp(y, by, by + bh);
      const distX = x - closestX;
      const distY = y - closestY;

      if (distX * distX + distY * distY < radius * radius) return true;
    }
    return false;
  }

  private buildWalkGrid(mapDef: ExplorationMap): void {
    const cell = 24;
    const cols = Math.max(1, Math.ceil(mapDef.width / cell));
    const rows = Math.max(1, Math.ceil(mapDef.height / cell));
    const blocked = new Uint8Array(cols * rows);
    const r = 16;
    for (let gy = 0; gy < rows; gy++) {
      for (let gx = 0; gx < cols; gx++) {
        const cx = gx * cell + cell / 2;
        const cy = gy * cell + cell / 2;
        const outside = cx < r || cy < r || cx > mapDef.width - r || cy > mapDef.height - r;
        blocked[gy * cols + gx] = outside || this.checkCollision(cx, cy, r) ? 1 : 0;
      }
    }
    this.walkGrid = { cols, rows, cell, blocked };
  }

  private worldToCell(x: number, y: number): { gx: number; gy: number } {
    const g = this.walkGrid!;
    return {
      gx: Phaser.Math.Clamp(Math.floor(x / g.cell), 0, g.cols - 1),
      gy: Phaser.Math.Clamp(Math.floor(y / g.cell), 0, g.rows - 1)
    };
  }

  private cellToWorld(gx: number, gy: number): { x: number; y: number } {
    const g = this.walkGrid!;
    return { x: gx * g.cell + g.cell / 2, y: gy * g.cell + g.cell / 2 };
  }

  private isFree(gx: number, gy: number): boolean {
    const g = this.walkGrid;
    if (!g) return false;
    if (gx < 0 || gy < 0 || gx >= g.cols || gy >= g.rows) return false;
    return g.blocked[gy * g.cols + gx] === 0;
  }

  private nearestFreeCell(gx: number, gy: number, maxRing = 6): { gx: number; gy: number } | null {
    if (this.isFree(gx, gy)) return { gx, gy };
    for (let ring = 1; ring <= maxRing; ring++) {
      let best: { gx: number; gy: number } | null = null;
      let bestD = Infinity;
      for (let dy = -ring; dy <= ring; dy++) {
        for (let dx = -ring; dx <= ring; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
          const nx = gx + dx;
          const ny = gy + dy;
          if (this.isFree(nx, ny)) {
            const d = dx * dx + dy * dy;
            if (d < bestD) { bestD = d; best = { gx: nx, gy: ny }; }
          }
        }
      }
      if (best) return best;
    }
    return null;
  }

  /**
   * Saca al jugador de una colisión tras aparecer en un spawn.
   *
   * `updatePlayer` valida cada paso por separado y un paso a 220 px/s dura unos
   * 3.5 px, muy por debajo del radio de colisión de 16 px: quien aparece
   * incrustado en una banda tiene *todos* los movimientos rechazados y queda
   * congelado sin forma de salir. Los spawns los escribe a mano quien autoriza la
   * aventura, así que esto no es defensivo, es un caso real.
   */
  private snapToWalkable(): void {
    if (!this.walkGrid) return;
    if (!this.checkCollision(this.player.x, this.player.y, 16)) return;
    const c = this.worldToCell(this.player.x, this.player.y);
    const free = this.nearestFreeCell(c.gx, c.gy, 8);
    if (!free) return;
    const w = this.cellToWorld(free.gx, free.gy);
    this.player.x = w.x;
    this.player.y = w.y;
  }

  private heur(ax: number, ay: number, bx: number, by: number): number {
    const dx = Math.abs(ax - bx);
    const dy = Math.abs(ay - by);
    return dx + dy + (1.414 - 2) * Math.min(dx, dy);
  }

  private findPath(sx: number, sy: number, tx: number, ty: number): Array<{ x: number; y: number }> | null {
    const g = this.walkGrid;
    if (!g) return null;
    const s = this.worldToCell(sx, sy);
    const tRaw = this.worldToCell(tx, ty);
    const targetWasFree = this.isFree(tRaw.gx, tRaw.gy);
    const goal = this.nearestFreeCell(tRaw.gx, tRaw.gy, 7);
    if (!goal) return null;
    const start = this.isFree(s.gx, s.gy) ? s : this.nearestFreeCell(s.gx, s.gy, 4);
    if (!start) return null;

    const n = g.cols * g.rows;
    const startIdx = start.gy * g.cols + start.gx;
    const goalIdx = goal.gy * g.cols + goal.gx;
    if (startIdx === goalIdx) {
      return targetWasFree ? [{ x: tx, y: ty }] : [this.cellToWorld(goal.gx, goal.gy)];
    }

    const gScore = new Float32Array(n).fill(Infinity);
    const fScore = new Float32Array(n).fill(Infinity);
    const came = new Int32Array(n).fill(-1);
    const closed = new Uint8Array(n);
    const inOpen = new Uint8Array(n);
    gScore[startIdx] = 0;
    fScore[startIdx] = this.heur(start.gx, start.gy, goal.gx, goal.gy);
    const open: number[] = [startIdx];
    inOpen[startIdx] = 1;
    const dirs = [[1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1], [1, 1, 1.414], [1, -1, 1.414], [-1, 1, 1.414], [-1, -1, 1.414]];

    let found = false;
    while (open.length) {
      let bi = 0;
      for (let i = 1; i < open.length; i++) if (fScore[open[i]] < fScore[open[bi]]) bi = i;
      const cur = open.splice(bi, 1)[0];
      inOpen[cur] = 0;
      if (cur === goalIdx) { found = true; break; }
      closed[cur] = 1;
      const cx = cur % g.cols;
      const cy = (cur / g.cols) | 0;
      for (const [dx, dy, cost] of dirs) {
        const nx = cx + dx;
        const ny = cy + dy;
        if (!this.isFree(nx, ny)) continue;
        if (dx !== 0 && dy !== 0 && (!this.isFree(cx + dx, cy) || !this.isFree(cx, cy + dy))) continue;
        const nIdx = ny * g.cols + nx;
        if (closed[nIdx]) continue;
        const tentative = gScore[cur] + cost;
        if (tentative < gScore[nIdx]) {
          came[nIdx] = cur;
          gScore[nIdx] = tentative;
          fScore[nIdx] = tentative + this.heur(nx, ny, goal.gx, goal.gy);
          if (!inOpen[nIdx]) { open.push(nIdx); inOpen[nIdx] = 1; }
        }
      }
    }
    if (!found) return null;

    const cells: number[] = [];
    let c = goalIdx;
    while (c !== -1) {
      cells.push(c);
      if (c === startIdx) break;
      c = came[c];
    }
    cells.reverse();
    const world = cells.map((idx) => this.cellToWorld(idx % g.cols, (idx / g.cols) | 0));
    if (world.length && targetWasFree) world[world.length - 1] = { x: tx, y: ty };
    return this.smoothPath(world);
  }

  private smoothPath(pts: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> {
    if (pts.length <= 2) return pts;
    const out: Array<{ x: number; y: number }> = [pts[0]];
    let i = 0;
    while (i < pts.length - 1) {
      let j = pts.length - 1;
      for (; j > i + 1; j--) {
        if (this.lineOfSight(pts[i], pts[j])) break;
      }
      out.push(pts[j]);
      i = j;
    }
    return out;
  }

  private lineOfSight(a: { x: number; y: number }, b: { x: number; y: number }): boolean {
    const g = this.walkGrid;
    if (!g) return false;
    const steps = Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y)) / (g.cell / 2));
    for (let s = 1; s < steps; s++) {
      const t = s / steps;
      const x = a.x + (b.x - a.x) * t;
      const y = a.y + (b.y - a.y) * t;
      if (this.checkCollision(x, y, 14)) return false;
    }
    return true;
  }

  private cancelClickMove(): void {
    this.movePath = [];
    this.pendingInteractionId = null;
    this.clearDestMarker();
  }

  private onReachDestination(): void {
    this.clearDestMarker();
    const id = this.pendingInteractionId;
    this.pendingInteractionId = null;
    if (id) this.triggerInteractionById(id);
  }

  private triggerInteractionById(id: string): void {
    const mapDef = this.currentMap();
    if (!mapDef) return;
    const inter = mapDef.interactions?.[id];
    if (!inter) return;
    if (!this.interactionAvailable(inter)) return;

    const ix = inter.position.x * mapDef.width;
    const iy = inter.position.y * mapDef.height;
    const ddx = ix - this.player.x;
    const ddy = iy - this.player.y;
    if (Math.abs(ddx) > Math.abs(ddy)) this.player.facing = ddx > 0 ? 'right' : 'left';
    else this.player.facing = ddy > 0 ? 'down' : 'up';

    if (inter.enabledIf && this.callbacks.onCheckCondition && !this.callbacks.onCheckCondition(inter.enabledIf)) {
      if (inter.disabledHint) this.callbacks.onBlocked(inter.disabledHint);
      return;
    }
    this.callbacks.onInteract(id);
  }

  private approachPoint(ix: number, iy: number, radius: number): { x: number; y: number } {
    const ang = Math.atan2(this.player.y - iy, this.player.x - ix);
    const d = Math.max(30, radius * 0.7);
    const ax = ix + Math.cos(ang) * d;
    const ay = iy + Math.sin(ang) * d;
    const c = this.worldToCell(ax, ay);
    const free = this.nearestFreeCell(c.gx, c.gy, 4);
    if (free) return this.cellToWorld(free.gx, free.gy);
    return { x: ax, y: ay };
  }

  private commandMoveTo(tx: number, ty: number, interactionId?: string): boolean {
    const path = this.findPath(this.player.x, this.player.y, tx, ty);
    if (!path || path.length === 0) {
      this.cancelClickMove();
      this.callbacks.onBlocked('No hay forma de llegar hasta ahí.');
      return false;
    }
    this.movePath = path;
    this.pendingInteractionId = interactionId ?? null;
    const end = path[path.length - 1];
    this.showDestMarker(end.x, end.y);
    return true;
  }

  private handleTap(wx: number, wy: number): void {
    if (!this.moveEnabled) return;
    const mapDef = this.currentMap();
    if (!mapDef) return;

    let best: ProximityInteraction | null = null;
    let bestD = Infinity;
    if (mapDef.interactions) {
      for (const inter of Object.values(mapDef.interactions)) {
        if (!this.interactionAvailable(inter)) continue;
        const ix = inter.position.x * mapDef.width;
        const iy = inter.position.y * mapDef.height;
        const dClick = Math.hypot(wx - ix, wy - iy);
        const hitR = Math.max(inter.radius ?? 60, 48);
        if (dClick < hitR && dClick < bestD) { bestD = dClick; best = inter; }
      }
    }

    if (best) {
      const ix = best.position.x * mapDef.width;
      const iy = best.position.y * mapDef.height;
      const dNow = Math.hypot(this.player.x - ix, this.player.y - iy);
      if (dNow <= (best.radius ?? 60)) {
        this.cancelClickMove();
        this.triggerInteractionById(best.id);
      } else {
        const ap = this.approachPoint(ix, iy, best.radius ?? 60);
        this.commandMoveTo(ap.x, ap.y, best.id);
      }
      return;
    }

    this.commandMoveTo(wx, wy);
  }

  private showDestMarker(x: number, y: number): void {
    const scene = this.sceneInstance;
    if (!scene) return;
    this.clearDestMarker();
    const c = scene.add.container(x, y).setDepth(DEPTH.prompt + 2);
    const g = scene.add.graphics();
    g.lineStyle(3, 0xffd479, 0.9);
    g.strokeCircle(0, 0, 10);
    g.fillStyle(0xffd479, 0.3);
    g.fillCircle(0, 0, 4);
    c.add(g);
    this.destMarker = c;
    if (!this.reducedMotion) {
      c.setScale(0.6);
      const t = scene.tweens.add({
        targets: c, scale: 1.3, alpha: 0, duration: 700, ease: 'Sine.easeOut',
        onComplete: () => this.clearDestMarker()
      });
      this.liveTweens.push(t);
    } else {
      scene.time.delayedCall(500, () => this.clearDestMarker());
    }
  }

  private clearDestMarker(): void {
    if (this.destMarker) { this.destMarker.destroy(); this.destMarker = null; }
  }

  setDialogueFraming(active: boolean): void {
    const scene = this.sceneInstance;
    if (!scene) return;
    const cam = scene.cameras.main;
    const targetY = active ? -150 : 0;
    if (this.reducedMotion) {
      cam.setFollowOffset(0, targetY);
      return;
    }
    const t = scene.tweens.addCounter({
      from: cam.followOffset.y, to: targetY, duration: 380, ease: 'Sine.easeOut',
      onUpdate: (tw) => cam.setFollowOffset(0, tw.getValue() ?? 0)
    });
    this.liveTweens.push(t);
  }

  private checkPortals(): void {
    const mapDef = this.currentMap();
    if (!mapDef?.portals) return;

    for (const portal of Object.values(mapDef.portals)) {
      if (portal.visibleIf && this.callbacks.onCheckCondition) {
        if (!this.callbacks.onCheckCondition(portal.visibleIf)) continue;
      }
      const px = portal.position.x * mapDef.width;
      const py = portal.position.y * mapDef.height;
      const hw = ((portal.size?.w ?? 0.08) * mapDef.width) / 2 + 24;
      const hh = ((portal.size?.h ?? 0.1) * mapDef.height) / 2 + 24;
      const inside = Math.abs(this.player.x - px) <= hw && Math.abs(this.player.y - py) <= hh;

      if (inside) {
        if (this.portalGuard === portal.targetMap) continue;
        this.portalGuard = this.currentMapId;
        this.loadMap(portal.targetMap, portal.targetSpawn);
        return;
      }
      if (this.portalGuard === portal.targetMap) this.portalGuard = null;
    }
  }

  private updateCompanion(dt: number): void {
    if (!this.companion.visible || !this.companion.sprite) return;
    const comp = this.def.exploration?.companion;
    if (!comp) return;

    const followDist = comp.followDistance ?? 50;
    const dx = this.player.x - this.companion.x;
    const dy = this.player.y - this.companion.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    const walkKey = `companion-${comp.animations.walk}`;
    const walking = this.companion.sprite.anims.currentAnim?.key === walkKey;
    const shouldWalk = walking ? dist > followDist * 0.7 : dist > followDist;

    if (shouldWalk && dist > 1) {
      const base = comp.speed ?? (this.def.exploration?.avatar.speed ?? 200) * 1.2;
      const ease = Math.min(2, Math.max(0.35, (dist - followDist * 0.6) / followDist));
      const nx = dx / dist;
      const ny = dy / dist;
      this.companion.x += nx * base * ease * dt;
      this.companion.y += ny * base * ease * dt;
      this.companion.sprite.setPosition(this.companion.x, this.companion.y);

      if (nx < -0.25) this.companion.sprite.flipX = true;
      else if (nx > 0.25) this.companion.sprite.flipX = false;

      if (!walking) this.companion.sprite.play(walkKey, true);
    } else if (walking) {
      this.companion.sprite.play(`companion-${comp.animations.idle}`, true);
    }
  }

  private checkProximity(): void {
    const mapDef = this.currentMap();
    if (!mapDef?.interactions) return;

    let closest: ProximityInteraction | null = null;
    let closestDist = Infinity;

    for (const inter of Object.values(mapDef.interactions)) {
      if (!this.interactionAvailable(inter)) continue;
      const ix = inter.position.x * mapDef.width;
      const iy = inter.position.y * mapDef.height;
      const dist = Math.sqrt((this.player.x - ix) ** 2 + (this.player.y - iy) ** 2);
      const radius = inter.radius ?? 60;

      if (dist < radius && dist < closestDist) {
        closest = inter;
        closestDist = dist;
      }
    }

    if (closest !== this.nearbyInteraction) {
      this.nearbyInteraction = closest;
      this.updatePromptBubble();
    }
  }

  private updatePromptBubble(): void {
    const scene = this.sceneInstance;
    if (!scene) return;

    if (this.promptBubble) {
      this.promptBubble.destroy();
      this.promptBubble = null;
    }

    if (!this.nearbyInteraction) return;

    const inter = this.nearbyInteraction;
    const container = scene.add.container(this.player.x, this.player.y - 60)
      .setDepth(DEPTH.prompt + 1);

    const bg = scene.add.rectangle(0, 0, 160, 32, 0x0a0f1e, 0.85)
      .setStrokeStyle(2, 0xffd479, 0.9);
    bg.setOrigin(0.5);
    container.add(bg);

    const text = scene.add.text(0, 0, `${inter.label}  [E]`, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#eaf0ff',
      align: 'center'
    }).setOrigin(0.5).setResolution(2);
    container.add(text);

    this.promptBubble = container;
  }

  private tryInteract(): void {
    if (!this.nearbyInteraction) return;
    const inter = this.nearbyInteraction;
    if (inter.enabledIf && this.callbacks.onCheckCondition) {
      if (!this.callbacks.onCheckCondition(inter.enabledIf)) {
        if (inter.disabledHint) this.callbacks.onBlocked(inter.disabledHint);
        return;
      }
    }
    this.callbacks.onInteract(inter.id);
  }

  private updateDepthSorting(): void {
    const playerY = this.player.y;

    for (const [_id, sprite] of this.objectSprites) {
      sprite.setDepth(sprite.y < playerY ? DEPTH.objects - 1 : DEPTH.objects + 1);
    }
    for (const [_id, sprite] of this.npcSprites) {
      sprite.setDepth(sprite.y < playerY ? DEPTH.npc - 1 : DEPTH.npc + 1);
    }
    if (this.player.sprite) {
      this.player.sprite.setDepth(DEPTH.player);
    }
    if (this.companion.sprite) {
      const dy = this.companion.y - playerY;
      if (dy < -10) this.companionDepth = DEPTH.companion - 1;
      else if (dy > 10) this.companionDepth = DEPTH.companion + 1;
      this.companion.sprite.setDepth(this.companionDepth);
    }
    if (this.promptBubble) {
      this.promptBubble.setPosition(this.player.x, this.player.y - 60);
    }
  }

  private currentMap(): ExplorationMap | null {
    if (!this.currentMapId) return null;
    return this.def.maps?.[this.currentMapId] ?? null;
  }

  setMoveEnabled(enabled: boolean): void {
    this.moveEnabled = enabled;
    if (!enabled) {
      this.cancelClickMove();
      this.touchJoystick.active = false;
      this.touchJoystick.dx = 0;
      this.touchJoystick.dy = 0;
    }
    if (!enabled && this.player.sprite) {
      const avatar = this.def.exploration?.avatar;
      if (avatar) {
        const idleKey = `avatar-${avatar.animations.idle}`;
        if (this.player.sprite.anims.currentAnim?.key !== idleKey) {
          this.player.sprite.play(idleKey, true);
        }
      }
    }
  }

  setObjectState(objectId: string, state: string): void {
    const mapDef = this.currentMap();
    const scene = this.sceneInstance;
    if (!mapDef || !scene) return;
    const obj = mapDef.objects?.[objectId];
    if (!obj) return;
    const stateDef = obj.states[state];
    if (!stateDef || !scene.textures.exists(stateDef.image)) return;

    const existing = this.objectSprites.get(objectId);
    const x = stateDef.position.x * mapDef.width;
    const y = stateDef.position.y * mapDef.height;

    if (existing) {
      existing.setTexture(stateDef.image);
      existing.setPosition(x, y);
      if (stateDef.scale) existing.setScale(stateDef.scale);
      if (!this.reducedMotion) this.celebrateObject(scene, existing, x, y, stateDef.scale ?? 1);
    }
  }

  /** Destello aditivo + pop de escala cuando un objeto cambia de estado. */
  private celebrateObject(
    scene: Phaser.Scene,
    sprite: Phaser.GameObjects.Image,
    x: number,
    y: number,
    baseScale: number
  ): void {
    const ring = scene.add.circle(x, y, 26, 0xffd479, 0.85);
    ring.setBlendMode(Phaser.BlendModes.ADD);
    ring.setDepth(sprite.depth + 1);
    scene.tweens.add({
      targets: ring,
      alpha: 0,
      scale: 3.4,
      duration: 520,
      ease: 'Cubic.Out',
      onComplete: () => ring.destroy()
    });
    scene.tweens.add({
      targets: sprite,
      scale: baseScale * 1.16,
      duration: 130,
      yoyo: true,
      ease: 'Sine.Out',
      onComplete: () => sprite.setScale(baseScale)
    });
  }

  setReducedMotion(value: boolean): void {
    this.reducedMotion = value;
  }

  /**
   * Proyecta un objeto del mapa a píxeles CSS relativos al contenedor del canvas.
   *
   * Phaser dibuja con `Scale.FIT` y centra el lienzo, así que hay dos saltos: de
   * mundo a píxeles del visor de la cámara, y de ahí al rectángulo real del canvas
   * en la página. Devuelve null si el objeto no está o salió de cuadro, para que
   * quien ancle algo encima sepa que debe ocultarlo.
   */
  getObjectScreenRect(
    objectId: string,
    anchoRelativo: number
  ): { left: number; top: number; width: number; height: number } | null {
    const scene = this.sceneInstance;
    const mapDef = this.currentMap();
    const sprite = this.objectSprites.get(objectId);
    if (!scene || !mapDef || !sprite || !sprite.visible) return null;

    const canvas = scene.game.canvas;
    const host = canvas.parentElement;
    const view = scene.cameras.main.worldView;
    if (!host || view.width <= 0 || view.height <= 0) return null;

    const box = canvas.getBoundingClientRect();
    const hostBox = host.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return null;

    const kx = box.width / view.width;
    const ky = box.height / view.height;
    const worldW = mapDef.width * anchoRelativo;
    const worldH = (worldW * 9) / 16;
    const offsetX = box.left - hostBox.left;
    const offsetY = box.top - hostBox.top;

    const left = (sprite.x - worldW / 2 - view.x) * kx + offsetX;
    const top = (sprite.y - worldH / 2 - view.y) * ky + offsetY;
    const width = worldW * kx;
    const height = worldH * ky;

    if (left + width < 0 || top + height < 0 || left > hostBox.width || top > hostBox.height) return null;
    return { left, top, width, height };
  }

  private clearTweens(): void {
    for (const t of this.liveTweens) {
      if (t && t.isPlaying()) t.remove();
    }
    this.liveTweens = [];
  }

  destroy(): void {
    window.removeEventListener('keydown', this.blockScrollKeys);
    document.removeEventListener('keydown', this.guardGameKeys);
    this.clearTweens();
    this.clearMap();
    if (this.player.sprite) { this.player.sprite.destroy(); this.player.sprite = null; }
    if (this.companion.sprite) { this.companion.sprite.destroy(); this.companion.sprite = null; }
    if (this.game) {
      this.game.destroy(true);
      this.game = null;
    }
    this.sceneInstance = null;
  }
}
